import APIError from '@api/exceptions/APIError';
import ProxyError from '@api/exceptions/ProxyError';

const CONFIG_BASE =
	typeof process !== 'undefined' && process.env && process.env.WORKER_BASE_URL
		? process.env.WORKER_BASE_URL
		: false;
const RUNTIME_ORIGIN =
	typeof window !== 'undefined' && window.location
		? window.location?.href?.split('/scanner')[0]
		: '';
const BASE_URL = CONFIG_BASE || RUNTIME_ORIGIN;

class API {
	static async request({ path, method = 'POST', headers, exceptionClass }) {
		try {
			const response = await fetch(path, {
				method,
				headers,
			});

			if (!response.ok) {
				const ExceptionClass = exceptionClass || APIError;

				// Check if it's a bot protection error by reading the response body
				if (response.status === 403) {
					try {
						const errorBody = await response.text();
						if (errorBody.includes('Bot protection detected')) {
							throw new ExceptionClass('Bot protection detected', response.status, true);
						}
					} catch (parseError) {
						// If we can't parse the body, fall through to regular error
					}
				}

				throw new ExceptionClass(response.statusText, response.status);
			}

			return response;
		} catch (e) {
			if (e instanceof APIError) {
				throw e;
			} else {
				throw new APIError(e.message);
			}
		}
	}

	static addQueryParams(url, params) {
		const endpoint = new URL(url);

		for (const paramKey in params) {
			if (params.hasOwnProperty(paramKey)) {
				endpoint.searchParams.append(paramKey, params[paramKey]);
			}
		}

		return endpoint.href;
	}

	static async fetchPage(url) {
		// In production, elementor.com may block self-requests (elementor.com -> elementor.com)
		// So we fetch directly from browser to avoid this issue
		// In staging, proxy works fine because it's different origin (stg.elementor.red -> elementor.com)
		const targetUrl = new URL(url);
		const isElementorDomain = 
			targetUrl.hostname === 'elementor.com' || 
			targetUrl.hostname === 'www.elementor.com';
		
		// Check if we're in production (elementor.com domain)
		const isProduction = 
			typeof window !== 'undefined' && 
			window.location && 
			(window.location.hostname === 'elementor.com' || window.location.hostname === 'www.elementor.com');

		// Only bypass proxy in production for elementor.com domains
		if (isProduction && isElementorDomain) {
			return API.handleFetchingSameDomainInProd(url);
		}

		// For other cases (staging or non-elementor domains), use proxy
		return API.request({
			method: 'GET',
			path: API.addQueryParams(`${BASE_URL}/proxy`, { url }),
			exceptionClass: ProxyError,
		});
	}

	static async handleFetchingSameDomainInProd(url) {
		try {
			// Fetch directly from browser (bypasses worker proxy)
			const response = await fetch(url, {
				method: 'GET',
				mode: 'cors',
				credentials: 'omit',
			});

			if (!response.ok) {
				throw new ProxyError(response.statusText, response.status);
			}

			return response;
		} catch (e) {
			if (e instanceof ProxyError) {
				throw e;
			}
			throw new ProxyError(e.message);
		}
	}
}

export default API;
